import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector } from '@/store/hooks';
import { useNotification } from '@/hooks/useNotification';
import { addProject, updateProjectImage } from '../../../apiServices/projectApi';
import { putToStorageDefault } from '../../../apiServices/storageApiService';
import {
  getParentId,
  getIsInWelcomeScreen,
  getMode,
  validateTitleField,
} from './NewItemModalUtilityFunctions';
import { Button } from '../../ui/Button';
import { Spinner } from '../../ui/Spinner';
import { Input } from '../../ui/Input';
import ImageSection from '../setPictureModal/ImagePicker';
import { getLowerCaseFileExtensionInFile } from '../../../util/UtilFunctions';
import { pdfToPng } from '../setPictureModal/SetPictureModalUtilFunctions';

interface NewItemModalProps {
  closeModal: () => void;
  updateIsQuerying: (querying: boolean) => void;
  refreshList: (selectItem?: unknown, sortIdx?: number, hard?: boolean) => void;
  welcomeScreenSelection?: string;
  location?: { pathname: string };
}

interface PictureProcess {
  status: string;
  data?: { picture?: File | Blob; croppedImage?: string; pdfFile?: File; name?: string };
}

export default function NewItemModal(props: NewItemModalProps) {
  const oldUserDetails = useAppSelector((state) => state.user.oldUserDetails) as { role?: { S: string } } | null;
  const userRole = oldUserDetails?.role?.S || 'user';
  const location = useLocation();
  const navigate = useNavigate();
  const { notify } = useNotification();
  const pathname = props.location?.pathname || location.pathname;

  const [activeRadio, setActiveRadio] = useState('folder');
  const [titleFieldValue, setTitleFieldValue] = useState('');
  const [pictureUploadingProcess, setPictureUploadingProcess] = useState<PictureProcess>({ status: 'initial' });
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | false>(false);
  const [croppedImageUrl, setCroppedImageUrl] = useState<string | undefined>();

  const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTitleFieldValue(event.target.value);
    setErrorMessage(false);
  };

  const updateItemCover = async (pictureProcess: PictureProcess, creatingProcess: { data: { response: string } }) => {
    try {
      props.updateIsQuerying(true);
      const storageResponse = await putToStorageDefault(pictureProcess.data!.picture!, '');
      props.updateIsQuerying(false);
      try {
        props.updateIsQuerying(true);
        await updateProjectImage(
          creatingProcess.data.response,
          titleFieldValue,
          storageResponse.key,
          pictureProcess.data!.name || '',
        );
        props.updateIsQuerying(false);
      } catch {
        props.updateIsQuerying(false);
        props.refreshList(false, undefined, true);
      }
    } catch {
      props.updateIsQuerying(false);
    }
  };

  const handleItemSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submitting) return;
    const mode = getMode(userRole, props.welcomeScreenSelection);
    const uploadingItem = mode === undefined ? activeRadio : mode;
    const validationError = validateTitleField(titleFieldValue);
    if (!validationError) {
      setErrorMessage(false);
      setSubmitting(true);
      let creatingProcess: { status: string; data?: { response: string } };
      try {
        props.updateIsQuerying(true);
        const parentId = getParentId(pathname);
        const addProjectResponse = await addProject({ title: titleFieldValue, type: uploadingItem, parent: parentId });
        creatingProcess = { status: 'success', data: addProjectResponse };
        props.updateIsQuerying(false);
      } catch {
        creatingProcess = { status: 'error' };
        props.updateIsQuerying(false);
        setSubmitting(false);
        notify({ type: 'error', message: `Could not add ${uploadingItem}` });
        return;
      }
      if (
        (pictureUploadingProcess.status === 'pictureCropped' || pictureUploadingProcess.status === 'pdfSelected') &&
        creatingProcess.data
      ) {
        await updateItemCover(pictureUploadingProcess, creatingProcess as { data: { response: string } });
        props.refreshList({ type: 'item', id: creatingProcess.data.response }, 0, true);
      } else {
        props.refreshList(false, undefined, true);
      }
      notify({ type: 'success', message: `Added ${uploadingItem}` });
      props.closeModal();
      if (getIsInWelcomeScreen(pathname)) {
        navigate('/in/folder/root');
      }
    } else {
      setErrorMessage(validationError);
    }
  };

  const updateCroppedImage = (croppedImage: string) => {
    setPictureUploadingProcess({
      status: 'pictureCropped',
      data: { picture: pictureUploadingProcess.data?.picture, croppedImage },
    });
  };

  const handlePictureChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = getLowerCaseFileExtensionInFile(event.target.files![0]) as File;
    const mimeTypeSplitArr = file.type.split('/');
    if (mimeTypeSplitArr[1] === 'gif') {
      notify({ type: 'info', message: ".gif files can't be set as cover images" });
    } else if (mimeTypeSplitArr[0] === 'image') {
      setPictureUploadingProcess({ status: 'pictureSelected', data: { picture: file, croppedImage: undefined } });
    } else if (mimeTypeSplitArr[1] === 'pdf') {
      setPictureUploadingProcess({ status: 'pdfSelected', data: { pdfFile: file, picture: undefined } });
      try {
        const newFile = await pdfToPng(file);
        setPictureUploadingProcess({ status: 'pdfSelected', data: { picture: newFile } });
        setCroppedImageUrl(URL.createObjectURL(file));
      } catch {
        notify({ type: 'error', message: 'Could not produce pdf thumbnail' });
      }
    } else {
      notify({ type: 'info', message: 'Given file is not an image or pdf' });
    }
  };

  const mode = getMode(userRole, props.welcomeScreenSelection);

  return (
    <div className="flex w-full flex-col gap-4">
      {!mode && (
        <div className="flex justify-center gap-4">
          {(['folder', 'project'] as const).map((type) => (
            <label key={type} className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="radio"
                name="itemType"
                checked={activeRadio === type}
                onChange={() => setActiveRadio(type)}
                className="accent-primary"
              />
              <span className="capitalize">{type}</span>
            </label>
          ))}
        </div>
      )}
      <div className="mx-auto w-full max-w-56">
        <Input
          type="text"
          placeholder="Title"
          value={titleFieldValue}
          onChange={handleTitleChange}
          error={!!errorMessage}
        />
        {errorMessage && <p className="mt-1 text-xs text-destructive">{errorMessage}</p>}
      </div>
      <div className="flex justify-center">
        <ImageSection
          useCase="createNew"
          pictureUploadingProcess={pictureUploadingProcess}
          currentUserInfo={oldUserDetails}
          updateCroppedImage={updateCroppedImage}
          croppedImageUrl={croppedImageUrl}
          handlePictureChange={handlePictureChange}
        />
      </div>
      <Button onClick={handleItemSubmit} disabled={submitting} className="mx-auto w-28">
        {submitting ? <Spinner size="sm" /> : 'Create'}
      </Button>
    </div>
  );
}
